/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import ModalComponent from '../../utils/modal';
import ImageViewComponent from './imageView';
import { FormInterface } from '../../../redux/interface';
import { setParameter } from '../../../redux/form/form.actions';
import { LoreeInteractiveEditorDashboardContentType } from '../../editor/editorUtilityFunctions/lintEditorType';
import { ReactComponent as HotspotIcon } from '../../../assets/icons/hotspotEditIcon.svg';
import { ToastManager } from '@crystaldelta/loree-ui-components';
import { isEmpty } from '../validate';

interface AddAreaComponentInterFace {
  type: string;
  index: number;
  editorConfig: LoreeInteractiveEditorDashboardContentType;
  isImageReplace: boolean;
}

const mapState = ({ form }: any) => ({
  dndParameters: form.dndParameters,
  fontSize: form.fontSize,
  errors: form.errors,
});

const mapDispatch = (dispatch: Function) => ({
  HandleParameters: (form: FormInterface) => dispatch(setParameter(form)),
});

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & AddAreaComponentInterFace;

const getSecureRandomFraction = (): number => {
  const cryptoApi = globalThis.crypto;

  if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
    return 0.5;
  }

  const randomBuffer = new Uint32Array(1);
  cryptoApi.getRandomValues(randomBuffer);
  return randomBuffer[0] / 0x100000000;
};

const getSecureRandomInRange = (min: number, max: number): number => {
  const lowerBound = Math.min(min, max);
  const upperBound = Math.max(min, max);

  if (lowerBound === upperBound) {
    return lowerBound;
  }

  return lowerBound + getSecureRandomFraction() * (upperBound - lowerBound);
};

const AddAreaComponent = (props: Props) => {
  const { type, index, dndParameters, fontSize, HandleParameters, editorConfig, isImageReplace } =
    props;
  const [screenType, setScreenType] = useState('view');
  const [show, setShow] = useState(false);
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [parameters, setParameters] = useState({ ...dndParameters[index] });
  const [actionHandlerState, setActionHandlerState] = useState(true);
  const [activeZone, setActiveZone] = useState('');
  const [imageDimension, setImageDimension] = useState({ width: 0, height: 0 });
  const [error, setError] = useState<number[]>([]);
  const [propId, setPropId] = useState('');
  const [hideEditor, setHideEditor] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [duplicateLabelError, setDuplicateLabelError] = useState(false);
  const [duplicateTextError, setDuplicateTextError] = useState(false);

  useEffect(() => {
    const current = { ...dndParameters[index] };

    if (current?.dropZone?.length) {
      const cleaned = current.dropZone.map((item: any, idx: number) => ({
        ...item,
        id: idx + 1,
      }));

      const hasDuplicate =
        new Set(current.dropZone.map((i: any) => i.id)).size !== current.dropZone.length;

      if (hasDuplicate) {
        const updated = [...dndParameters];
        updated[index] = { ...current, dropZone: cleaned };

        HandleParameters({
          dndParameters: updated,
          errors: { ...error },
        });
      }

      setParameters({ ...current, dropZone: cleaned });
    } else {
      setParameters({ ...current });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dndParameters, index]);

  const stripHTML = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.trim() || '';
  };
  const validateUniqueLabelAndText = (label: string, text: string, id: number) => {
    if (id === null || isNaN(id)) {
      return true;
    }
    if (parameters.dropZone.length <= 1) {
      return true;
    }
    const cleanLabel = label.trim();
    const cleanText = stripHTML(text);

    const duplicateLabel = parameters.dropZone.some((item: any) => {
      if (item.id === id) return false;

      const itemLabel = item.data?.label?.trim();
      if (!cleanLabel || !itemLabel) return false;
      return itemLabel === cleanLabel;
    });

    const duplicateText = parameters.dropZone.some((item: any) => {
      if (item.id === id) return false;

      const itemText = stripHTML(item.data?.text || '');
      if (!cleanText || !itemText) return false;
      return itemText === cleanText;
    });

    setDuplicateLabelError(duplicateLabel);
    setDuplicateTextError(duplicateText);

    setDuplicateLabelError(duplicateLabel);
    setDuplicateTextError(duplicateText);
    return !duplicateLabel && !duplicateText;
  };

  const addDropZone = () => {
    const width = imageDimension?.width > 0 ? `${Math.min(150, imageDimension.width)}px` : '150px';

    const height =
      imageDimension?.height > 0 ? `${Math.min(150, imageDimension.height)}px` : '150px';
    const id =
      propId !== '' ? propId : Math.max(0, ...parameters.dropZone.map((item: any) => item.id)) + 1;
    const container = document.querySelector('.image-container');
    const image = document.getElementById('dndImage');

    let x = 50;
    let y = 50;

    if (container && image) {
      const scrollLeft = container.scrollLeft;
      const scrollTop = container.scrollTop;

      const visibleWidth = container.clientWidth;
      const visibleHeight = container.clientHeight;
      const imageWidth = image.offsetWidth;
      const imageHeight = image.offsetHeight;
      const boxWidth = parseInt(width.replace('px', '')) || 100;
      const boxHeight = parseInt(height.replace('px', '')) || 100;
      const minX = scrollLeft;
      const minY = scrollTop;
      const maxX = Math.min(scrollLeft + visibleWidth - boxWidth, imageWidth - boxWidth);
      const maxY = Math.min(scrollTop + visibleHeight - boxHeight, imageHeight - boxHeight);
      let found = false;
      let xTemp = 0;
      let yTemp = 0;
      for (let i = 0; i < 50 && !found; i++) {
        xTemp = getSecureRandomInRange(minX, maxX);
        yTemp = getSecureRandomInRange(minY, maxY);

        const isOverlap = parameters.dropZone.some((z: any) => {
          const zx = z.data.x;
          const zy = z.data.y;
          const zw = parseInt(z.data.width);
          const zh = parseInt(z.data.height);
          return !(
            xTemp + boxWidth < zx ||
            xTemp > zx + zw ||
            yTemp + boxHeight < zy ||
            yTemp > zy + zh
          );
        });

        if (!isOverlap) {
          found = true;
        }
      }

      x = xTemp;
      y = yTemp;
    }
    parameters.dropZone = [
      ...parameters.dropZone,
      {
        id: id,
        data: {
          x: x,
          y: y,
          label: '',
          text: '<p></p>',
          width: width,
          height: height,
          imgWidth: imageDimension?.width,
          imgHeight: imageDimension?.height,
        },
      },
    ];
    const isFirstDropZone = parameters.dropZone.length === 1;
    if (isFirstDropZone) {
      setTimeout(() => {
        const container = document.querySelector('.image-container');
        const newElement = document.getElementById(`rnd_${id}`);

        if (container && newElement) {
          newElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      }, 0);
    }
    switchScreen();
    setPropId('');
    setParameters({ ...parameters });
    setActiveZone(String(id));
    handleDropZoneAction();
    setActionHandlerState(false);
  };
  const handleDropZoneAction = () => {
    const elementsIndex = parameters.dropZone.findIndex(
      (element: { id: number }) => element.id === parseInt(activeZone),
    );
    if (elementsIndex === -1) return;

    const dropZoneData = parameters.dropZone.map((a: any) => ({
      ...a,
      data: { ...a.data },
    }));

    if (screenType === 'view' || screenType === 'editDetails') {
      dropZoneData[elementsIndex].data.label = label;
      dropZoneData[elementsIndex].data.text = text;
    } else {
      dropZoneData.splice(elementsIndex, 1);
      setActiveZone('');
      setActionHandlerState(true);
    }
    setParameters((prev: any) => ({
      ...prev,
      dropZone: dropZoneData,
    }));
  };
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const deleteDropZone = () => {
    const elementsIndex = parameters.dropZone.findIndex(
      (element: { id: number }) => element.id === parseInt(activeZone),
    );
    if (elementsIndex === -1) return;

    const dropZoneData = parameters.dropZone.map((a: any) => ({
      ...a,
      data: { ...a.data },
    }));

    dropZoneData.splice(elementsIndex, 1);
    setPropId(activeZone);
    setActiveZone('');
    setActionHandlerState(true);
    setParameters((prev: any) => ({
      ...prev,
      dropZone: dropZoneData,
    }));
    setHideEditor(true);
    setDuplicateLabelError(false);
    setDuplicateTextError(false);
  };

  const switchScreen = () => {
    setError([]);
    setLabel('');
    setText('');
    setScreenType('view');
  };

  useEffect(() => {
    if (activeZone === null || activeZone === undefined) {
      setLabel('');
      setText('');
      return;
    }

    const selected = parameters.dropZone.find(
      (element: { id: number }) => element.id === Number(activeZone),
    );
    setLabel(selected?.data?.label || '');
    setText(selected?.data?.text || '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeZone]);
  const setDimension = (obj: any, idx: number) => {
    const elementsIndex = parameters.dropZone.findIndex((element: any) => element.id === idx);
    let dimensions = document.getElementById('dndImage')?.getBoundingClientRect();
    const data = parameters.dropZone.map((a: {}) => ({ ...a }));
    const percentX = (obj.x / (dimensions?.width || 1)) * 100;
    const percentY = (obj.y / (dimensions?.height || 1)) * 100;
    data[elementsIndex].data = {
      ...data[elementsIndex].data,
      x: percentX,
      y: percentY,
      width: obj.w,
      height: obj.h,
      imgWidth: dimensions?.width,
      imgHeight: dimensions?.height,
    };
    setParameters((prev: any) => ({
      ...prev,
      dropZone: prev.dropZone.map((zone: any) =>
        zone.id === idx
          ? {
              ...zone,
              data: {
                ...zone.data,
                x: obj.x,
                y: obj.y,
                width: obj.w,
                height: obj.h,
                imgWidth: dimensions?.width || 1,
                imgHeight: dimensions?.height || 1,
              },
            }
          : zone,
      ),
    }));
  };

  const handleSave = () => {
    const isValid = validateUniqueLabelAndText(label, text, parseInt(activeZone));
    if (!isValid) {
      return;
    }
    const errorIndexes: number[] = [];

    const updatedDropZones = parameters.dropZone.map((zone: any, idx: number) => {
      const zoneError: any = {};
      if (!zone?.data?.label?.trim()) {
        zoneError.label = 'Label is required';
      }
      const textValue = zone?.data?.text || '';
      console.log('textValue', textValue);
      if (isEmpty(textValue)) {
        zoneError.text = 'Text is required';
      }
      if (Object.keys(zoneError).length > 0) {
        errorIndexes.push(idx + 1);
      }
      return {
        ...zone,
        error: zoneError,
      };
    });
    if (errorIndexes.length > 0) {
      setError(errorIndexes);
      setToastMessage(`${type} is empty. Label and text are required.`);
      setShowToast(true);
      return;
    }
    const content = [...dndParameters];
    content[index] = { ...parameters, dropZone: updatedDropZones };
    HandleParameters({
      dndParameters: content,
      errors: {},
    });

    handleModalClose();
  };
  const updateActiveDropZone = (id: number) => {
    setParameters((prev: any) => ({
      ...prev,
      dropZone: prev.dropZone.map((zone: any) => ({
        ...zone,
      })),
    }));
  };
  const setHandleConfirm = () => {
    return handleSave;
  };

  const handleModalClose = () => {
    setDuplicateLabelError(false);
    setDuplicateTextError(false);
    setDuplicateLabelError(false);
    setDuplicateTextError(false);
    setShow(false);
    setParameters({ ...dndParameters[index] });
    switchScreen();
    setActionHandlerState(true);
    setActiveZone('');
  };

  const getTitle = () => {
    const isDnd = type === 'DragandDrop';

    return `Add or Edit ${isDnd ? 'Drop Zones' : 'Hotspots'}`;
  };

  return (
    <>
      <button
        className='footer-cancel-btn HotspotsButton'
        onClick={() => setShow(true)}
        disabled={isImageReplace}
        style={{
          cursor: isImageReplace ? 'not-allowed' : 'pointer',
        }}
      >
        Add/Edit {type === 'DragandDrop' ? 'Drop Zones' : 'Hotspots'}
      </button>
      <ModalComponent
        dialogClassName='addordropmodal'
        show={show}
        handleCancel={handleModalClose}
        size='lg'
        modalDetails={{
          title: (
            <div
              style={{
                fontFamily: 'Lexend',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#585858',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <HotspotIcon style={{ marginRight: '10px' }} />
              {getTitle()}
            </div>
          ),
          body: (
            <ImageViewComponent
              type={type}
              parameters={parameters}
              fontSize={fontSize}
              setScreenType={(type: string) => setScreenType(type)}
              label={{ value: label, setLabel: setLabel }}
              text={{ value: text, setText: setText }}
              setDimension={setDimension}
              icons={{
                status: actionHandlerState,
                setStatus: setActionHandlerState,
              }}
              dropZone={{
                activeIndex: parseInt(activeZone),
                setActiveIndex: setActiveZone,
              }}
              setImageDimension={setImageDimension}
              errorHandler={{ error: error, setError: setError }}
              editorConfig={editorConfig}
              addDropZone={() => addDropZone()}
              updateActiveDropZone={(id: number) => updateActiveDropZone(id)}
              setIsDeleted={deleteDropZone}
              Editor={{
                hideEditor: hideEditor,
                setHideEditor: setHideEditor,
              }}
              setParameters={setParameters}
              errorOnCurrentZone={{
                duplicateLabelError: duplicateLabelError,
                duplicateTextError: duplicateTextError,
                setDuplicateLabelError: setDuplicateLabelError,
                setDuplicateTextError: setDuplicateTextError,
              }}
              validateUniqueLabelAndText={(label: string, text: string, id: number) =>
                validateUniqueLabelAndText(label, text, id)
              }
            />
          ),
        }}
        confirmButton={{
          label: 'Save',
          disabled: false,
          handleConfirm: setHandleConfirm(),
        }}
        cancelButton={{
          label: 'Cancel',
          handleCancel: handleModalClose,
        }}
      />
      {showToast && (
        <ToastManager
          toastType='error'
          toastMessage={toastMessage}
          closeButton
          closeToast={() => setShowToast(false)}
          zIndex={100000000000}
        />
      )}
    </>
  );
};

export default connector(AddAreaComponent);
